import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, ArrowRight, UserCircle, Shield, Zap, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './AuthPage.css';

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, isAuthenticated } = useAuth();
  
  const [activeTab, setActiveTab] = useState('login');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [toast, setToast] = useState(null);
  
  // Form states
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false,
  });
  
  const [errors, setErrors] = useState({});

  // Check if user is already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/history';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Show toast notification
  const showToast = (message, type = 'success') => {
    const displayMessage = message || (type === 'error' ? 'An error occurred' : 'Success');
    setToast({ message: displayMessage, type });
    setTimeout(() => setToast(null), 5000);
  };

  // Validate login form
  const validateLogin = () => {
    const newErrors = {};
    
    if (!loginData.email.trim()) {
      newErrors.loginEmail = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(loginData.email)) {
      newErrors.loginEmail = 'Please enter a valid email';
    }
    
    if (!loginData.password) {
      newErrors.loginPassword = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate register form
  const validateRegister = () => {
    const newErrors = {};
    
    if (!registerData.name.trim()) {
      newErrors.registerName = 'Name is required';
    } else if (registerData.name.length < 2) {
      newErrors.registerName = 'Name must be at least 2 characters';
    }
    
    if (!registerData.email.trim()) {
      newErrors.registerEmail = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(registerData.email)) {
      newErrors.registerEmail = 'Please enter a valid email';
    }
    
    if (!registerData.password) {
      newErrors.registerPassword = 'Password is required';
    } else if (registerData.password.length < 8) {
      newErrors.registerPassword = 'Password must be at least 8 characters';
    }
    
    if (registerData.password !== registerData.confirmPassword) {
      newErrors.registerConfirmPassword = 'Passwords do not match';
    }
    
    if (!registerData.agreeTerms) {
      newErrors.registerTerms = 'You must agree to the terms';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle login submit
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!validateLogin()) return;
    
    setIsLoading(true);
    const result = await login({
      email: loginData.email,
      password: loginData.password,
    });
    
    if (result.success) {
      showToast('Welcome back!');
    } else {
      showToast(result.error, 'error');
    }
    setIsLoading(false);
  };

  // Handle register submit
  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!validateRegister()) return;
    
    setIsLoading(true);
    const result = await register({
      name: registerData.name,
      email: registerData.email,
      password: registerData.password,
    });
    
    if (result.success) {
      showToast('Account created successfully!');
    } else {
      showToast(result.error, 'error');
    }
    setIsLoading(false);
  };

  // Handle continue as guest
  const handleContinueAsGuest = () => {
    const from = location.state?.from?.pathname || '/';
    navigate(from);
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1>Welcome to Omini Bridge</h1>
          <p>Sign in to track your exchanges or continue as a guest</p>
        </div>

        <div className="auth-tabs">
          <button
            className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => setActiveTab('login')}
          >
            Sign In
          </button>
          <button
            className={`auth-tab ${activeTab === 'register' ? 'active' : ''}`}
            onClick={() => setActiveTab('register')}
          >
            Create Account
          </button>
        </div>

        {activeTab === 'login' ? (
          <form className="auth-form" onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="login-email">Email <span>*</span></label>
              <div className="input-wrapper">
                <Mail className="input-icon" />
                <input
                  type="email"
                  id="login-email"
                  placeholder="Enter your email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  className={errors.loginEmail ? 'error' : ''}
                />
              </div>
              {errors.loginEmail && <span className="field-error">{errors.loginEmail}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="login-password">Password <span>*</span></label>
              <div className="input-wrapper">
                <Lock className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="login-password"
                  placeholder="Enter your password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  className={errors.loginPassword ? 'error' : ''}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.loginPassword && <span className="field-error">{errors.loginPassword}</span>}
            </div>

            <div className="auth-options">
              <label className="remember-me">
                <input
                  type="checkbox"
                  checked={loginData.rememberMe}
                  onChange={(e) => setLoginData({ ...loginData, rememberMe: e.target.checked })}
                />
                Remember me
              </label>
              <Link to="/forgot-password" className="forgot-password">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              className="auth-submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="spinner" />
              ) : (
                <>
                  Sign In <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
        ) : (
          <form className="auth-form" onSubmit={handleRegister}>
            <div className="form-group">
              <label htmlFor="register-name">Full Name <span>*</span></label>
              <div className="input-wrapper">
                <User className="input-icon" />
                <input
                  type="text"
                  id="register-name"
                  placeholder="Enter your full name"
                  value={registerData.name}
                  onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                  className={errors.registerName ? 'error' : ''}
                />
              </div>
              {errors.registerName && <span className="field-error">{errors.registerName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="register-email">Email <span>*</span></label>
              <div className="input-wrapper">
                <Mail className="input-icon" />
                <input
                  type="email"
                  id="register-email"
                  placeholder="Enter your email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  className={errors.registerEmail ? 'error' : ''}
                />
              </div>
              {errors.registerEmail && <span className="field-error">{errors.registerEmail}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="register-password">Password <span>*</span></label>
              <div className="input-wrapper">
                <Lock className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="register-password"
                  placeholder="Create a password (min 8 characters)"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  className={errors.registerPassword ? 'error' : ''}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.registerPassword && <span className="field-error">{errors.registerPassword}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="register-confirm-password">Confirm Password <span>*</span></label>
              <div className="input-wrapper">
                <Lock className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="register-confirm-password"
                  placeholder="Confirm your password"
                  value={registerData.confirmPassword}
                  onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                  className={errors.registerConfirmPassword ? 'error' : ''}
                />
              </div>
              {errors.registerConfirmPassword && (
                <span className="field-error">{errors.registerConfirmPassword}</span>
              )}
            </div>

            <div className="form-group">
              <label className="remember-me">
                <input
                  type="checkbox"
                  checked={registerData.agreeTerms}
                  onChange={(e) => setRegisterData({ ...registerData, agreeTerms: e.target.checked })}
                />
                I agree to the <Link to="/terms">Terms of Service</Link> and{' '}
                <Link to="/privacy">Privacy Policy</Link>
              </label>
              {errors.registerTerms && <span className="field-error">{errors.registerTerms}</span>}
            </div>

            <button
              type="submit"
              className="auth-submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="spinner" />
              ) : (
                <>
                  Create Account <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
        )}

        <div className="auth-divider">or</div>

        <div className="guest-option">
          <button className="guest-btn" onClick={handleContinueAsGuest}>
            <UserCircle size={20} />
            Continue as Guest
          </button>
        </div>

        <div className="auth-footer">
          <p>
            {activeTab === 'login' ? (
              <>
                Don&apos;t have an account?{' '}
                <button onClick={() => setActiveTab('register')} className="link-button">
                  Sign up
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button onClick={() => setActiveTab('login')} className="link-button">
                  Sign in
                </button>
              </>
            )}
          </p>
          <div className="security-badges">
            <span className="security-badge">
              <Shield size={12} /> Secure
            </span>
            <span className="security-badge">
              <Zap size={12} /> Instant
            </span>
          </div>
        </div>
      </div>

      {toast && (
        <div className={`toast ${toast.type}`}>
          {toast.type === 'success' ? (
            <CheckCircle size={20} style={{ color: '#065f46' }} />
          ) : (
            <XCircle size={20} style={{ color: '#991b1b' }} />
          )}
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
};

export default AuthPage;
